import { sortByAPY, sortByUsdValue } from '@/domain/common/sorters'
import { OpenDialogFunction } from '@/domain/hooks/useDialog'
import { useCardSolverConfigs, useStoreHydrated } from '@/domain/state'
import { CardRouteBadge } from '@/features/card/components/CardRouteBadge'
import { getPooledSpendCardRouteBadge } from '@/features/card/utils/cardSolverRouteMatching'
import { depositDialogConfig } from '@/features/dialogs/deposit/DepositDialog'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { ApyTooltip } from '@/ui/molecules/apy-tooltip/ApyTooltip'
import { ActionsCell } from '@/ui/molecules/data-table/components/ActionsCell'
import { CompactValueCell } from '@/ui/molecules/data-table/components/CompactValueCell'
import { TokenWithLogo } from '@/ui/molecules/data-table/components/TokenWithLogo'
import { ResponsiveDataTable } from '@/ui/organisms/responsive-data-table/ResponsiveDataTable'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { CheckmarkCell } from '@/ui/molecules/data-table/components/CheckmarkCell'
import { ApyWithRewardsCell } from '@/features/my-portfolio/components/ApyWithRewardsCell'
import { Deposit } from '../../logic/assets'
import { ColumnDefinition } from '@/ui/molecules/data-table/types'
import { useMemo } from 'react'
import { usePointsData } from '@/domain/points/pointsData/usePointsData'
import { mockNativeToWHYPEOverride } from '@/utils/address'
import { useAccount, useChainId } from 'wagmi'
import { getDisplayToken } from '@/domain/hooks/useDisplayToken'

export interface DepositTableProps {
  assets: Deposit[]
  openDialog: OpenDialogFunction
}

export function DepositTable({ assets, openDialog }: DepositTableProps) {
  const { address } = useAccount()
  const filteredAssets = assets.filter((asset) => asset.token.symbol !== TokenSymbol('USDXL'))
  const { getByAddress, error: pointsDataError, isLoading: isPointsDataLoading } = usePointsData()
  const { byAddress: cardSolverConfigsByAddress } = useCardSolverConfigs()
  const isStoreHydrated = useStoreHydrated()
  const hasPointsError = !isPointsDataLoading && !!pointsDataError?.message
  const chainId = useChainId()
  const cardSolverConfigs = useMemo(
    () => (isStoreHydrated && address ? Object.values(cardSolverConfigsByAddress[address.toLowerCase()] ?? {}) : []),
    [address, cardSolverConfigsByAddress, isStoreHydrated],
  )

  const columnDefinition = useMemo<{ [key: string]: ColumnDefinition<Deposit> }>(
    () => ({
      symbol: {
        header: 'Assets',
        renderCell: ({ token, reserveStatus, isCombinedBalance, aTokenAddress }) => {
          const displayToken = getDisplayToken(chainId, token)
          const cardRouteBadge = getPooledSpendCardRouteBadge(cardSolverConfigs, aTokenAddress)
          return (
            <div className="flex items-center gap-2">
              <TokenWithLogo
                token={displayToken}
                reserveStatus={reserveStatus}
                isCombinedBalance={isCombinedBalance}
                hideSymbol
              />
              <CardRouteBadge badge={cardRouteBadge} />
            </div>
          )
        },
      },
      inWallet: {
        header: 'In Wallet',
        sortable: true,
        sortingFn: (a, b) => sortByUsdValue(a.original, b.original, 'balance'),
        headerAlign: 'right',
        renderCell: ({ token, balance }, mobileViewOptions) => (
          <CompactValueCell token={token} value={balance} mobileViewOptions={mobileViewOptions} hideEmpty />
        ),
      },
      deposit: {
        header: 'Deposit',
        sortable: true,
        sortingFn: (a, b) => sortByUsdValue(a.original, b.original, 'deposit'),
        headerAlign: 'right',
        showOnMobile: true,
        renderCell: ({ token, deposit }, mobileViewOptions) => (
          <CompactValueCell token={token} value={deposit} mobileViewOptions={mobileViewOptions} hideEmpty />
        ),
      },
      apy: {
        header: <ApyTooltip variant="supply">APY</ApyTooltip>,
        headerAlign: 'right',
        sortable: true,
        showOnMobile: true,
        sortingFn: (a, b) => sortByAPY(a.original.supplyAPY, b.original.supplyAPY),
        renderCell: ({ supplyAPY, token }, mobileViewOptions) => {
          const points = getByAddress({
            type: 'pooled',
            action: 'deposit',
            address: mockNativeToWHYPEOverride(token.address),
          })
          return (
            <ApyWithRewardsCell
              action="deposit"
              asset={token.symbol}
              apy={supplyAPY}
              points={points}
              mobileViewOptions={mobileViewOptions}
              pointsError={hasPointsError}
              pointsLoading={isPointsDataLoading}
            />
          )
        },
      },
      collateral: {
        header: 'Can be collateral',
        headerAlign: 'right',
        renderCell: ({ usageAsCollateralEnabled }) => (
          <CheckmarkCell usageAsCollateralEnabled={usageAsCollateralEnabled} />
        ),
      },
      actions: {
        header: '',
        renderCell: ({ token, reserveStatus }) => {
          return (
            <ActionsCell>
              <Button
                size="sm"
                className="w-full md:w-fit"
                onClick={() => {
                  openDialog(depositDialogConfig, { token })
                }}
                disabled={reserveStatus === 'frozen' || token.symbol === TokenSymbol('USDXL')}
              >
                Deposit
              </Button>
            </ActionsCell>
          )
        },
      },
    }),
    [openDialog, getByAddress, hasPointsError, isPointsDataLoading, chainId, cardSolverConfigs],
  )
  return (
    <Panel collapsibleOptions={{ collapsible: true, collapsibleAbove: 'md' }} className="bg-panel md:px-3">
      <Panel.Header>
        <Panel.Title className="text-xl md:px-3" gradient>
          Available to deposit
        </Panel.Title>
      </Panel.Header>

      <Panel.Content>
        <ResponsiveDataTable
          gridTemplateColumnsClassName="grid-cols-[4fr_repeat(3,_3fr)_2fr_3fr]"
          columnDefinition={columnDefinition}
          data={filteredAssets}
        />
      </Panel.Content>
    </Panel>
  )
}
